import React, { useState, useRef, useEffect, useLayoutEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

/** Flatten React children so SelectItems wrapped in <>...</> are visible to display + list logic. */
const flattenSelectChildren = (children) => {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === Fragment) {
      out.push(...flattenSelectChildren(child.props.children));
    } else {
      out.push(child);
    }
  });
  return out;
};

/**
 * Shared dropdown look — see dashboard-design-spec.md “Visual tokens for future pages”.
 * Warm hairline + surface white + dark low-opacity focus ring, matching the dashboard filters.
 */
const TRIGGER_CLASS =
  'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-hairline bg-surface px-3 text-sm font-medium text-[#18181B] placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50';

const PANEL_CLASS =
  'fixed z-[60] w-max max-h-60 overflow-auto rounded-lg border border-hairline bg-surface p-1 shadow-card';

// Simple, functional Select component
export const FunctionalSelect = ({ 
  value, 
  onValueChange, 
  placeholder = "All",
  children,
  className = "",
  disabled = false,
  triggerClassName = "",
  label = null,
  leading = null,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = selectRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const place = () => {
      const button = selectRef.current?.querySelector('button');
      const panel = panelRef.current;
      if (!button || !panel) return;
      const rect = button.getBoundingClientRect();
      const width = Math.max(rect.width, panel.offsetWidth);
      let left = rect.left;
      const maxLeft = window.innerWidth - width - 8;
      if (left > maxLeft) left = Math.max(8, maxLeft);
      panel.style.top = `${rect.bottom + 6}px`;
      panel.style.left = `${left}px`;
      panel.style.minWidth = `${rect.width}px`;
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen]);

  const handleSelect = (newValue) => {
    onValueChange(newValue);
    setIsOpen(false);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  // Find the display text for the current value
  const getDisplayText = () => {
    if (!value && value !== 0) return placeholder;

    const selectedChild = flattenSelectChildren(children).find(
      (child) =>
        React.isValidElement(child) &&
        child.type === SelectItem &&
        String(child.props.value) === String(value)
    );

    return selectedChild ? selectedChild.props.children : placeholder;
  };

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      <button
        type="button"
        id={id}
        className={`${TRIGGER_CLASS} ${triggerClassName}`}
        onClick={toggleOpen}
        disabled={disabled}
      >
        <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
          {leading ?? (label ? (
            <span className="flex-shrink-0 whitespace-nowrap font-normal text-zinc-400">{label}:</span>
          ) : null)}
          <span className="block min-w-0 truncate">
            {getDisplayText()}
          </span>
        </span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-[#71717A]" />
      </button>
      
      {isOpen && createPortal(
        <div ref={panelRef} className={PANEL_CLASS}>
          {flattenSelectChildren(children).map((child, idx) => {
            if (React.isValidElement(child) && child.type === SelectItem) {
              return React.cloneElement(child, {
                key: child.key ?? child.props.value ?? idx,
                isSelected: String(child.props.value) === String(value),
                onSelect: handleSelect
              });
            }
            return child;
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export const SelectItem = ({ className = '', children, value, onSelect, isSelected, ...props }) => (
  <div
    className={`relative flex w-full cursor-pointer select-none items-center whitespace-nowrap rounded-md py-2 pl-8 pr-3 text-sm outline-none hover:bg-[#E8EEF5] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ${
      isSelected ? 'bg-[#E8EEF5] font-medium text-[#18181B]' : 'text-[#3F3F46]'
    } ${className}`}
    onClick={() => onSelect(value)}
    {...props}
  >
    {isSelected && (
      <Check className="absolute left-2 h-4 w-4 text-[#18181B]" />
    )}
    {children}
  </div>
);

// Legacy exports for backward compatibility
export const Select = FunctionalSelect;
export const SelectTrigger = () => null;
export const SelectContent = () => null;
export const SelectValue = () => null;
